import { createApi } from '@reduxjs/toolkit/query/react';
import axiosBaseQuery from 'rtk/query/axiosBaseQuery';
import config from 'config/index';

// types
import type { TTemplate } from 'types/template/template.types';
import { EUserType } from 'types/accessControll.types';
import type { TInterviewClass } from 'types/interview.types';
import type { TChatCollection, TInterviewScoreTable, TInterviewScoreTableDetail } from 'types/scores.types';
import type { EScoringBuildSystem } from 'pages/score/ScoreInterviewPage';

const reducerPath = 'scores' as const;
const path = 'v1/scores' as const;

export const scoresApi = createApi({
  reducerPath: reducerPath,
  baseQuery: axiosBaseQuery({
    baseUrl: `${config.VITE_BACKEND_URL}/${path}`,
  }),
  endpoints: (builder) => ({
    getScoreInterviewById: builder.query<
      {
        template: TTemplate;
        interview: TInterviewClass;
        scoringBuildSystem: EScoringBuildSystem;
      },
      {
        interviewId: string;
        scoringBuildSystem?: EScoringBuildSystem;
      }
    >({
      query: (x) => {
        console.log('scoreInterviewById', x.interviewId);
        return {
          url: `/score/${x.interviewId}?scoringBuildSystem=${x.scoringBuildSystem}`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    getRecordingSignedUrl: builder.query<{ url: string; expires_at: string }, string>({
      query: (answerId) => ({
        url: `/recordings/${answerId}/signed-url`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
    }),

    // Durable summary media link → fresh signed URL (token = last path segment of detail.media.link).
    getRecordingMediaUrl: builder.query<{ url: string; expires_at: string; kind?: string }, string>({
      query: (token) => ({
        url: `/recordings/media-url?token=${encodeURIComponent(token)}`,
        method: 'GET',
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
    }),

    // Manual retry of a failed/missing recording transcription (relays to chat-backend backfill).
    retryRecordingTranscription: builder.mutation<{ status: string }, string>({
      query: (answerId) => ({
        url: `/recordings/${answerId}/transcribe`,
        method: 'POST',
        headers: { 'X-Access-Control': EUserType.HUBERT },
      }),
    }),

    manuallyScoreInterviewById: builder.mutation<
      {
        score_update_result: {
          status: number;
          data: { message: string };
        };
        integration_update_result: {
          status: number;
          data: {
            status: number;
            platform: string;
            message: string;
          };
        };
      },
      {
        interviewId: string;
        scoreTable: TInterviewScoreTable;
        scoreTableDetails: TInterviewScoreTableDetail[];
        chat: TChatCollection[];
        updateIntegration: boolean;
      }
    >({
      query: (x) => {
        console.log('scoreInterviewById', x.interviewId);
        return {
          url: `/manual/${x.interviewId}`,
          method: 'POST',
          data: x,
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),

    reScoreInterviewById: builder.mutation<Record<string, string>, { interviewId: string }>({
      query: (x) => {
        console.log('reScoreInterviewById', x.interviewId);
        return {
          url: `/re-score/${x.interviewId}`,
          method: 'GET',
          headers: { 'X-Access-Control': EUserType.HUBERT },
        };
      },
    }),
  }),
});

export const {
  // Queries
  useGetScoreInterviewByIdQuery,
  useGetRecordingSignedUrlQuery,
  useGetRecordingMediaUrlQuery,
  useRetryRecordingTranscriptionMutation,
  // Mutations
  useManuallyScoreInterviewByIdMutation,
  useReScoreInterviewByIdMutation,
} = scoresApi;
